import { useCallback, useRef, useState } from 'react';

import { getAnalytics, logEvent } from 'firebase/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { setMessage } from '../../toasts/toastSlice';
import { setOrdersModalConfig, setOrdersToDeliverFilters } from '../orderSlice';
import {
  dataUrlToBlob,
  handleAxiosErrorMessage,
  normalizeName,
} from '../../../utils';
import { NOTIFICATION_TYPES } from '../../../config/consts/commons';
import {
  VALID_RECIPIENT_NAME_REGEX_PATTERN,
  VALID_RECIPIENT_DOCUMENT_REGEX_PATTERN,
} from '../../../config/consts/orders';

import useUploadDeliveredOrderPhoto from './useUploadDeliveredOrderPhoto';
import useConfirmPickupFromConsumer from './useConfirmPickupFromConsumer';
import useUploadOrderSignature from './useUploadOrderSignature';
import useEcommerceFields from './useEcommerceFields';
import useDeliveryGeolocation from './useDeliveryGeolocation';
import { RAPPI_ECOMMERCE_KEY } from '../../../config/ecommerceFieldsConfig';
import {
  generateDeliveryReceiptImage,
  buildDeliveryReceiptData,
} from '../utils';
import { getDeliveryReceiptTemplate } from '../utils/deliveryReceiptTemplate';

export default function useOrdersToDeliverPickupType11Modal() {
  const { t, i18n } = useTranslation([
    'system',
    'description',
    'button',
    'validation',
  ]);
  const { ordersToDeliverFilters, ordersModalConfig, confirmationAnalytics } =
    useSelector((state) => state.orders);
  const { order } = ordersModalConfig;

  const { config, validateRequiredFields } = useEcommerceFields(
    order?.ecommerce_name || 'default'
  );

  const [step, setStep] = useState(1);
  const {
    location,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    validateLocation,
    resetGeolocation,
  } = useDeliveryGeolocation();

  const [comment, setComment] = useState('');
  const [recipient, setRecipient] = useState('');
  const [recipientDocument, setRecipientDocument] = useState('');
  const [recipientRelationship, setRecipientRelationship] = useState('');
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [rawSignatureDataUrl, setRawSignatureDataUrl] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);

  const isSubmittingRef = useRef(false);
  const uploadedPhotoUrlRef = useRef(null);
  const uploadedReceiptUrlRef = useRef(null);

  const uploadDeliveredOrderPhotoMutation = useUploadDeliveredOrderPhoto();
  const confirmPickupFromConsumerMutation = useConfirmPickupFromConsumer();
  const uploadOrderSignatureMutation = useUploadOrderSignature();
  const analytics = getAnalytics();
  const dispatch = useDispatch();

  const validateRecipientFields = useCallback(() => {
    const cleanName = normalizeName(recipient);
    let isRecipientNameInvalid = false;
    let isRecipientDocumentInvalid = false;

    if (recipient) {
      isRecipientNameInvalid =
        !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(cleanName);
    }

    if (recipientDocument) {
      isRecipientDocumentInvalid =
        !VALID_RECIPIENT_DOCUMENT_REGEX_PATTERN.test(recipientDocument);
    }

    return (
      isRecipientNameInvalid ||
      isRecipientDocumentInvalid ||
      validateRequiredFields({
        recipientDocument,
        recipientRelationship,
        signature: rawSignatureDataUrl,
      })
    );
  }, [
    recipient,
    recipientDocument,
    recipientRelationship,
    rawSignatureDataUrl,
    validateRequiredFields,
  ]);

  const computeRulesStep1 = useCallback(
    ({ file, isFileCompressing }) => {
      return !file || isFileCompressing || validateLocation();
    },
    [validateLocation]
  );

  const computeRulesStep2 = useCallback(
    ({ file, isFileCompressing }) => {
      return (
        !recipient ||
        !file ||
        isFileCompressing ||
        validateLocation() ||
        validateRecipientFields()
      );
    },
    [recipient, validateLocation, validateRecipientFields]
  );

  const resetModal = useCallback(() => {
    const { show, ...rest } = ordersModalConfig;
    setStep(1);
    setComment('');
    setRecipient('');
    setRecipientDocument('');
    setRecipientRelationship('');
    setRawSignatureDataUrl(null);
    setSignatureModalOpen(false);
    setIsSubmitting(false);
    setShowSubmitError(false);
    isSubmittingRef.current = false;
    uploadedPhotoUrlRef.current = null;
    uploadedReceiptUrlRef.current = null;
    resetGeolocation();
    dispatch(setOrdersModalConfig({ ...rest, show: false }));
  }, [ordersModalConfig, dispatch, resetGeolocation]);

  const uploadSignatureFile = async ({ id_order }) => {
    const isRappi =
      order?.ecommerce_name?.toLowerCase().includes(RAPPI_ECOMMERCE_KEY) ??
      false;
    const useReceiptTemplate = config.fields?.includes('signature') && !isRappi;

    let sigFile;
    if (useReceiptTemplate) {
      const receiptData = buildDeliveryReceiptData({
        order,
        recipient,
        recipientDocument,
        recipientRelationship,
        signatureDataUrl: rawSignatureDataUrl,
        signatureDate: new Date().toISOString(),
      });
      const receiptBlob = await generateDeliveryReceiptImage(receiptData, {
        t,
        localeKey: i18n.language,
      });
      sigFile = new File([receiptBlob], 'comprovante-entrega.jpeg', {
        type: 'image/jpeg',
      });
    } else {
      const blob = dataUrlToBlob(rawSignatureDataUrl);
      sigFile = new File([blob], 'signature.jpeg', { type: 'image/jpeg' });
    }

    const sigFormData = new FormData();
    sigFormData.append('file', sigFile);

    if (!uploadedReceiptUrlRef.current) {
      const response = await uploadOrderSignatureMutation.mutateAsync({
        id_order,
        formData: sigFormData,
      });
      uploadedReceiptUrlRef.current = response.data;
    }

    return uploadedReceiptUrlRef.current;
  };

  const submitDelivered = async ({ file }) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const { id_order, order_code } = order;

    setShowSubmitError(false);
    const formData = new FormData();
    formData.append('file', file);
    setIsSubmitting(true);
    try {
      if (!uploadedPhotoUrlRef.current) {
        const uploadResponse =
          await uploadDeliveredOrderPhotoMutation.mutateAsync({
            id_order,
            formData,
          });
        uploadedPhotoUrlRef.current = uploadResponse.data;
      }
      const photoUrl = uploadedPhotoUrlRef.current;

      const payload = {
        photo_link: photoUrl,
      };

      const trimmedComment = comment?.trim();
      if (trimmedComment) {
        payload.comment = trimmedComment;
      }

      if (location.latitude && location.longitude) {
        payload.latitude = parseFloat(location.latitude);
        payload.longitude = parseFloat(location.longitude);
      } else {
        payload.address_location = customLocation;
      }

      await confirmPickupFromConsumerMutation.mutateAsync({
        idOrder: id_order,
        payload,
      });

      isSubmittingRef.current = false;
      resetModal();
      dispatch(setOrdersToDeliverFilters({ ...ordersToDeliverFilters }));
      dispatch(
        setMessage({
          body: t('ORDER_WITHDRAW_SUCCESS', {
            ns: 'system',
            order: order_code,
          }),
          type: NOTIFICATION_TYPES.SUCCESS,
        })
      );
      logEvent(analytics, confirmationAnalytics || 'pickup_type11_confirm', {});
    } catch (err) {
      const message = handleAxiosErrorMessage(err);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setShowSubmitError(true);
      dispatch(
        setMessage({
          body: t(message, { ns: 'system' }),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
    }
  };

  const handleSignatureConfirm = (signature) => {
    setRawSignatureDataUrl(signature);
    setSignatureModalOpen(false);
  };

  const loadReceiptTemplate = useCallback(() => {
    getDeliveryReceiptTemplate(t, i18n.language).catch(() => {});
  }, [t, i18n.language]);

  return {
    t,
    step,
    setStep,
    config,
    computeRulesStep1,
    computeRulesStep2,
    location,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    comment,
    setComment,
    recipient,
    setRecipient,
    recipientDocument,
    setRecipientDocument,
    recipientRelationship,
    setRecipientRelationship,
    signatureModalOpen,
    setSignatureModalOpen,
    signatureData: rawSignatureDataUrl,
    isSubmitting,
    showSubmitError,
    submitDelivered,
    handleSignatureConfirm,
    resetModal,
    loadReceiptTemplate,
  };
}
